import React, { useEffect, useRef, useState } from 'react';
import { MTGCard } from 'types/interfaces/mtgCard';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import { useIsMobile } from 'hooks/useIsMobile';
import ProgressBar from 'components/ProgressBar';
import ResultCard from './ResultCard';
import './ResultsGrid.css';

interface ResultsGridProps {
  cards: MTGCard[];
  cardSize: CardSize;
  /**
   * True whenever the rows on screen do not answer the filters that are set —
   * including the debounce window before a request has even been issued. Computed
   * once in SearchWorkbenchPage so the grid and the toolbar cannot disagree.
   */
  isResultsPending: boolean;
  isFetching: boolean;
  error: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /** Serialized request. Changing it means a different result set entirely. */
  queryKey: string;
  onClearAll: () => void;
  /** Server-side total, for the progress rail. Undefined until the first page lands. */
  totalCount?: number;
  /**
   * The trade filters as they stood for THIS result set. The grid needs them for
   * one reason: a zero-result page must be able to say which trade filter removed
   * the card, rather than leaving it to look like the card does not exist.
   */
  availableToSend?: boolean;
  availableToReceive?: boolean;
  /** Turns both trade filters off, leaving every other filter alone. */
  onClearTradeFilters?: () => void;
}

export const ResultsGrid: React.FC<ResultsGridProps> = ({
  cards,
  cardSize,
  isResultsPending,
  isFetching,
  error,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  queryKey,
  onClearAll,
  totalCount,
  availableToSend = false,
  availableToReceive = false,
  onClearTradeFilters,
}) => {
  const isMobile = useIsMobile();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  // One menu at a time: the grid owns which card has it open, so opening one
  // necessarily closes any other. Keyed by grid position, not scryfallId — the
  // same printing can legitimately appear twice in a result set, and keying on
  // the id alone opens every duplicate's menu at once.
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Latest values for the observer callback, so the observer itself only has to
  // be rebuilt when the query changes.
  const loadMoreRef = useRef(onLoadMore);
  const canLoadMoreRef = useRef(false);
  loadMoreRef.current = onLoadMore;
  canLoadMoreRef.current = hasNextPage && !isFetchingNextPage && !isFetching;

  // A filter change is a new result set: drop the hover, return to the top, and
  // rebuild the observer. Without the rebuild the sentinel can fire against the
  // previous query and append rows that do not belong to the current filters —
  // which is exactly what makes a rendered row count disagree with the total.
  useEffect(() => {
    setHoveredCard(null);
    setOpenMenuKey(null);
    gridRef.current?.parentElement?.scrollTo({ top: 0 });
  }, [queryKey]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canLoadMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { threshold: 0.1, rootMargin: '200px' },
    );

    observer.observe(node);
    // Managed by an effect rather than a callback ref: React ignores the return
    // value of a callback ref, so that pattern never disconnects and leaks.
    return () => observer.disconnect();
  }, [queryKey, hasNextPage]);

  if (error) {
    return (
      <div className="results-empty">
        <p>Something went wrong loading results.</p>
        <p className="results-empty-hint">Please try again in a moment.</p>
      </div>
    );
  }

  // Pending comes before the zero-result branch on purpose. It used to be keyed on
  // isLoading, which is false during the debounce window, so an empty `cards` there
  // fell through and rendered "No cards match these filters" on essentially every
  // filter change — before a request had even been sent.
  if (isResultsPending) {
    return (
      <>
        <div className="results-loading">
          <ProgressBar label="Loading results" />
        </div>
        <div className={`results-grid size-${cardSize.toLowerCase()}`} aria-busy={isResultsPending}>
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="result-card-skeleton" />
          ))}
        </div>
      </>
    );
  }

  if (cards.length === 0) {
    // A trade filter is the one filter that removes a card for a reason the user
    // cannot see on the card itself, so it gets named explicitly. Without this a
    // searched-for card just silently is not there.
    const tradeExplanation = availableToSend && availableToReceive
      ? 'Available to send and Available to receive are both on, so only cards someone wants and someone offers are shown.'
      : availableToSend
        ? 'Available to send is on, so only cards someone wants are shown.'
        : availableToReceive
          ? 'Available to receive is on, so only cards someone offers for trade are shown.'
          : null;

    const clearTradeLabel = availableToSend && availableToReceive
      ? 'Turn off both trade filters'
      : availableToSend
        ? 'Turn off Available to send'
        : 'Turn off Available to receive';

    return (
      <div className="results-empty">
        <p>No cards match these filters.</p>
        {tradeExplanation && <p className="results-empty-hint">{tradeExplanation}</p>}
        <div className="results-empty-actions">
          {tradeExplanation && onClearTradeFilters && (
            <button type="button" className="results-empty-clear" onClick={onClearTradeFilters}>
              {clearTradeLabel}
            </button>
          )}
          <button type="button" className="results-empty-clear" onClick={onClearAll}>
            Clear filters
          </button>
        </div>
      </div>
    );
  }

  // Never let the rail claim fewer results than are already on screen, which
  // would render a >100% bar if a total ever lagged the rows behind it.
  const total = totalCount === undefined ? undefined : Math.max(totalCount, cards.length);

  return (
    <>
      <div
        ref={gridRef}
        className={`results-grid size-${cardSize.toLowerCase()}`}
        aria-busy={isResultsPending}
      >
        {cards.map((card, index) => {
          const scryfallId =
            (card as unknown as { scryfallId?: string }).scryfallId ?? card.scryfallID;
          // Position-scoped so a printing appearing twice in one result set
          // hovers and opens its menu independently per tile.
          const cellKey = `${scryfallId}-${index}`;
          return (
            <ResultCard
              key={cellKey}
              card={card}
              isHovered={hoveredCard === cellKey}
              onHover={() => setHoveredCard(cellKey)}
              onLeave={() => setHoveredCard(null)}
              isMobile={isMobile}
              isMenuOpen={openMenuKey === cellKey}
              onOpenMenu={() => {
                // Opening a menu clears hover elsewhere, so two cards are never
                // both showing state at once.
                setOpenMenuKey(cellKey);
                setHoveredCard(cellKey);
              }}
              onCloseMenu={() => setOpenMenuKey(null)}
            />
          );
        })}
      </div>

      {hasNextPage && <div ref={sentinelRef} className="results-load-more" />}

      {/* Progress rail. The sentinel above still auto-loads on scroll — this is
          an additional affordance and a position readout, not a replacement. */}
      {total !== undefined && total > 0 && (
        <div className="results-rail">
          <div className="results-rail-status">
            Showing {cards.length.toLocaleString()} of {total.toLocaleString()}
          </div>
          <div
            className="results-rail-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={cards.length}
          >
            <div
              className="results-rail-fill"
              style={{ width: `${Math.min(100, (cards.length / total) * 100)}%` }}
            />
          </div>
          {hasNextPage && (
            <button
              type="button"
              className="results-rail-more"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage
                ? 'Loading…'
                : `Load ${Math.max(0, total - cards.length).toLocaleString()} more`}
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default ResultsGrid;
